import React, { forwardRef } from "react";
import classNames from "classnames";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../Tooltip/Tooltip";

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string | JSX.Element;
  error?: string | JSX.Element;
  startAddon?: React.ReactNode;
  endAddon?: React.ReactNode;
  suffixComponent?: React.ReactNode;
  containerClassName?: string;
  inputClassName?: string;
  dataCy?: string;
  tooltip?: {
    content: string;
    icon?: string; // defaults to 'icon-[ph--info]'
  };
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      id,
      name,
      label,
      type = "text",
      error,
      autoComplete,
      required = false,
      endAddon,
      startAddon,
      suffixComponent,
      disabled = false,
      containerClassName,
      inputClassName,
      tooltip,
      dataCy,
      ...props
    }: TextInputProps,
    ref,
  ) => {
    const rightAddon = suffixComponent ?? endAddon;

    return (
      <div className={classNames("min-w-0 w-full font-sans", containerClassName)}>
        {label && (
          <div
            className={classNames(
              "mb-1.5 flex items-center gap-2 text-sm font-medium",
              disabled ? "opacity-50" : "opacity-100",
              "text-primary-700 dark:text-primary-300",
            )}
          >
            {label}
            {tooltip && (
              <Tooltip placement="top" trigger="hover">
                <TooltipTrigger>
                  <div
                    className={classNames(
                      tooltip.icon || "icon-[ph--info]",
                      "h-4 w-4 text-primary-400 dark:text-primary-500 cursor-help",
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>{tooltip.content}</TooltipContent>
              </Tooltip>
            )}
            {required && (
              <span className="ml-1 text-error-500 dark:text-error-400">*</span>
            )}
          </div>
        )}

        <div className="relative flex min-w-0">
          {startAddon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500">
              {startAddon}
            </div>
          )}
          <input
            ref={ref}
            id={id}
            name={name}
            type={type}
            autoComplete={autoComplete}
            required={required}
            disabled={disabled}
            data-cy={dataCy}
            aria-label={typeof label === "string" ? label : undefined}
            aria-invalid={Boolean(error) || undefined}
            {...props}
            className={classNames(
              "min-h-10 min-w-0 flex-1 rounded-lg px-4 py-[1px] text-sm transition-all",
              "border border-solid font-sans",
              // Base gradient and shadow
              "bg-white dark:bg-transparent bg-gradient-to-b from-white to-primary-50/50 dark:from-content-900 dark:to-primary-950/70",
              "text-primary-700 dark:text-content-100",
              "border-primary-200/80 dark:border-primary-800/70",
              "shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)]",
              "dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.24)]",
              // Hover state
              "hover:border-primary-300 dark:hover:border-primary-700",
              "hover:shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]",
              "dark:hover:shadow-[inset_0_1px_3px_rgba(0,0,0,0.28)]",
              // Focus state
              "focus:border-primary-400 dark:focus:border-primary-400",
              "focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.06),0_0_0_3px_rgba(167,80,53,0.16)]",
              "dark:focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.24),0_0_0_3px_rgba(167,80,53,0.28)]",
              "focus:outline-none",
              // Placeholder
              "placeholder:text-primary-400 dark:placeholder:text-content-300",
              // Disabled state
              disabled &&
                "cursor-not-allowed opacity-50 bg-primary-50/50 dark:bg-primary-900/30 hover:shadow-none dark:hover:shadow-none",
              // Error state
              error &&
                "border-error-300 dark:border-error-700 shadow-[inset_0_1px_2px_rgba(220,38,38,0.1)] dark:shadow-[inset_0_1px_2px_rgba(220,38,38,0.2)]",
              // End addon adjustment
              startAddon && "pl-10",
              rightAddon && "pr-12",
              inputClassName,
            )}
          />

          {rightAddon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <div
                className={classNames(
                  "pointer-events-none select-none text-sm font-medium text-primary-500 dark:text-primary-400",
                  disabled && "opacity-50",
                  error && "text-error-500 dark:text-error-400",
                )}
              >
                {rightAddon}
              </div>
            </div>
          )}
        </div>

        {error && (
          <p className="mt-1.5 text-sm font-medium text-error-600 dark:text-error-400">
            {error}
          </p>
        )}
      </div>
    );
  },
);

TextInput.displayName = "TextInput";
